import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import { requirementsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CommentThread from './review/CommentThread';
import CommentComposer from './review/CommentComposer';

/**
 * CommentsTab (#358) — a dedicated Comments tab on the item-detail page, so every
 * item (not just a container via its Breakdown) can show and add comments in one
 * place. Renders the SINGLE item's thread with author name/initials/hover (the same
 * enriched CommentThread the Breakdown view uses) plus a composer to add a comment,
 * reply, resolve, escalate or delete.
 *
 * Data path: reuses `GET /items/{id}/breakdown` (getReview) and takes just this
 * item's thread (`threads[itemId]`), which the backend already enriches with
 * author display name/email. Mounted lazily by its TabPanel like the other tabs.
 */
const CommentsTab = ({ itemId, onCountChange, onMutated }) => {
  const auth = useAuth();
  const user = auth && auth.user;
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);

  // #387: report the live comment count up to ItemDetails, so the Comments tab's
  // position (first once > 0) and "(n)" badge track adds/deletes made right here
  // without a reload. Only fires once the thread has actually loaded (not on the
  // initial null), so it never races the parent's own seed fetch to 0.
  useEffect(() => {
    if (comments && onCountChange) onCountChange(comments.length);
  }, [comments, onCountChange]);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await requirementsApi.getReview(itemId);
      setComments((data && data.threads && data.threads[itemId]) || []);
    } catch (err) {
      setError(`Could not load comments: ${err.message}`);
      // null (not []) so the dedicated error/Retry view below actually renders —
      // an empty array is truthy for `!comments`, which previously made that
      // branch dead code and left a failed load with no retry affordance
      // (bug found while adding coverage, wave 4 / #438).
      setComments(null);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  // #360: deep-link from a "new comment" notification. The URL carries a
  // `#comment-<id>` hash; once the thread is loaded, scroll that comment into view
  // and briefly highlight it (mirrors BreakdownTab's effect, for leaf items whose
  // comments live here rather than in a Breakdown tab). Best-effort.
  const deepLinkedRef = useRef(null);
  useEffect(() => {
    if (!comments || typeof window === 'undefined') return undefined;
    const m = (window.location.hash || '').match(/^#comment-(.+)$/);
    if (!m) return undefined;
    const cid = m[1];
    if (deepLinkedRef.current === cid) return undefined;
    if (!comments.some((c) => c && c.comment_id === cid)) return undefined;
    deepLinkedRef.current = cid;
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${cid}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevBg = el.style.backgroundColor;
      el.style.transition = 'background-color 0.6s ease';
      el.style.backgroundColor = 'rgba(255, 214, 0, 0.35)';
      setTimeout(() => { el.style.backgroundColor = prevBg; }, 1600);
    }, 80);
    return () => clearTimeout(t);
  }, [comments]);

  const handleAdd = useCallback(async (body) => {
    const c = await requirementsApi.addComment(itemId, body, {});
    // #821: signal BEFORE touching state. This runs even when the component has already
    // been unmounted by a tab switch, whereas setComments and the effect that reports the
    // count both need another render and are lost. That loss is the reported bug: add a
    // comment, switch to History immediately, and the timeline never refreshes.
    if (onMutated) onMutated();
    setComments((prev) => [...(prev || []), c]);
    setComposing(false);
  }, [itemId, onMutated]);

  const handleReply = useCallback(async (parentId, body) => {
    const c = await requirementsApi.addComment(itemId, body, { parentCommentId: parentId });
    if (onMutated) onMutated();   // #821 — see the note on the top-level add
    setComments((prev) => [...(prev || []), c]);
  }, [itemId, onMutated]);

  const handleAction = useCallback(async (commentId, action, opts = {}) => {
    const updated = await requirementsApi.patchComment(commentId, action, opts);
    setComments((prev) => (prev || []).map((c) => (
      c.comment_id === commentId ? { ...c, ...updated } : c
    )));
  }, []);

  const handleDelete = useCallback(async (commentId) => {
    try {
      await requirementsApi.deleteComment(commentId);
      setComments((prev) => (prev || []).filter((c) => c.comment_id !== commentId));
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not delete comment: ${err.message}`);
    }
  }, []);

  const handleEscalate = useCallback(async (commentId, fields) => {
    try {
      const res = await requirementsApi.escalateComment(commentId, fields);
      const crId = res && res.change_request && res.change_request.id;
      setComments((prev) => (prev || []).map((c) => (
        c.comment_id === commentId ? { ...c, escalated_cr_id: crId } : c
      )));
    } catch (err) {
      setError(`Could not escalate comment: ${err.message}`);
    }
  }, []);

  if (loading && !comments) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="comments-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !comments) {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box data-testid="comments-tab" sx={{ py: 1 }}>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {composing ? (
        <CommentComposer
          placeholder="Add a comment — plain text."
          onCancel={() => setComposing(false)}
          onSave={handleAdd}
        />
      ) : (
        <Button size="small" variant="outlined" onClick={() => setComposing(true)}
                data-testid="comments-add" sx={{ mb: 1.5 }}>
          + Comment
        </Button>
      )}
      {(comments && comments.length > 0) ? (
        <CommentThread
          comments={comments}
          currentUserId={user && user.id}
          onAction={handleAction}
          onReply={handleReply}
          onEscalate={handleEscalate}
          onDelete={handleDelete}
        />
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="comments-empty">
          No comments yet.
        </Typography>
      )}
    </Box>
  );
};

CommentsTab.propTypes = {
  itemId: PropTypes.string,
  onCountChange: PropTypes.func,
  onMutated: PropTypes.func,
};

export default CommentsTab;
